/* eslint-disable jsx-a11y/no-autofocus */
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import {
  AlertCircle, Eye, EyeOff, HelpCircle, Search,
} from 'react-feather';
import './input.scss';
import clsx from 'clsx';
import Icon, { IconColors, IconSizes } from '../icon';
import Tooltip from '../tooltip';

const InputTypes = {
  TEXT: 'text',
  NUMBER: 'number',
  EMAIL: 'email',
  PASSWORD: 'password',
  SEARCH: 'search',
};

const InputTestIds = {
  INPUT: 'input',
  LABEL: 'label',
  ERROR: 'error',
};

const invalidCharacterArray = ['e', 'E', '+'];

const Input = React.forwardRef(({
  type, className, placeholder, value, label, isDisabled, isReadOnly, hintText, isInvalid,
  leftAddOn, rightAddOn, prefix, suffix, onChange, onBlur, searchAction, autoComplete, inputProps,
  multiline, labelHint, disablePaste, multilineRowsCount, suffixTooltip, min, max, allowDecimal,
  maxCharAllowed, autoFocus, onClick, onKeyUp, onInput, testIds,
}, ref) => {
  const classNames = clsx('input-cont', className);
  const wrapperClassNames = clsx('input-wrapper', { disabled: isDisabled, error: isInvalid, readOnly: isReadOnly });
  const elTabIndex = isDisabled ? -1 : 0;
  const [inputType, setInputType] = useState(type);
  const { name, title, ariaLabel } = inputProps;

  const getLabel = () => (label ? (
    <div className="label">
      <span className="label-items" data-testid={testIds[InputTestIds.LABEL]}>{label}</span>
      {labelHint
        && (
          <Tooltip description={labelHint} className="label-items">
            <Icon icon={HelpCircle} size={IconSizes.SMALL} color={IconColors.GREY} />
          </Tooltip>
        )}
    </div>
  ) : null);

  const getLeftAddon = () => (leftAddOn ? <span className="input-addon left-add-on">{leftAddOn}</span> : null);

  const getRightAddon = () => (rightAddOn ? <span className="input-addon right-add-on">{rightAddOn}</span> : null);

  const getHintText = () => (hintText ? <p className="hint-text">{hintText}</p> : null);

  const getPrefix = () => (<span className="input-morphemes">{prefix}</span>);

  const getSuffix = () => (<span className="input-morphemes">{suffix}</span>);

  const handleOnPaste = (event) => disablePaste && event.preventDefault();

  // TODO: Move logic to custom useKeyPress hook
  const handleKeyDown = (event, cb) => {
    const { target, key } = event;

    if (maxCharAllowed && target.value.length >= maxCharAllowed) {
      event.preventDefault();
    }

    if (type === InputTypes.NUMBER) {
      const { valueAsNumber } = target;

      // Avoid user to type exponential 'e' in number input.
      if (invalidCharacterArray.includes(key)) {
        event.preventDefault();
      }

      if (!allowDecimal && key === '.') {
        event.preventDefault();
      }

      // Avoid user to type minus when min is greater or equals to 0
      // Avoid user to type dot when allowDecimal is false
      if (min >= 0 && key === '-') {
        event.preventDefault();
      }

      // Avoid user to type values higher or lower than min, max values
      if (!Number.isNaN(valueAsNumber) && (valueAsNumber < min || valueAsNumber > max)) {
        event.preventDefault();
      }
    }

    if (key === 'Enter' && cb && typeof cb === 'function') {
      cb();
    }
  };

  const handleOnChange = (e) => {
    const { target } = e;
    const event = {
      ...e,
      target: {
        ...target,
        value: target.value,
        valueAsNumber: target.valueAsNumber,
      },
    };

    if (type === InputTypes.NUMBER) {
      const newValue = target.valueAsNumber;

      // Parse value to integer when allowDecimal is false
      if (!allowDecimal && target.value?.includes('.')) {
        event.target.valueAsNumber = parseInt(newValue, 10);
      }

      // Handle min max conditions, if user copy paste values
      if (Number.isNaN(newValue)) {
        event.target.valueAsNumber = '';
      } else if (newValue < min) {
        event.target.valueAsNumber = min;
      } else if (newValue > max) {
        event.target.valueAsNumber = max;
      }
    }

    onChange(event);
  };

  const renderPrefix = () => (
    prefix && getPrefix()
  );

  const renderSuffix = () => {
    const getErrorAlertIcon = () => (
      <span className="input-morphemes">
        <Icon icon={AlertCircle} color={IconColors.ERROR} size={IconSizes.SMALL} />
      </span>
    );

    const getErrorSuffix = () => (
      suffixTooltip ? (
        <Tooltip description={suffixTooltip}>{getErrorAlertIcon()}</Tooltip>
      ) : getErrorAlertIcon()
    );

    const getSearchSuffix = () => (
      <span className="input-morphemes" onClick={searchAction} onKeyDown={(e) => handleKeyDown(e, searchAction)}>
        <Icon
          className="icon-cta"
          tabIndex={elTabIndex}
          icon={Search}
          color={IconColors.GREY}
          size={IconSizes.MEDIUM}
        />
      </span>
    );

    const getPasswordSuffix = () => {
      const togglePassword = () => {
        if (inputType === InputTypes.PASSWORD) {
          setInputType(InputTypes.TEXT);
        } else {
          setInputType(InputTypes.PASSWORD);
        }
      };

      return (
        <span
          className="input-morphemes"
          onClick={togglePassword}
          onKeyDown={(e) => handleKeyDown(e, togglePassword)}
        >
          <Icon
            className="icon-cta"
            tabIndex={elTabIndex}
            icon={
              inputType === InputTypes.PASSWORD ? EyeOff : Eye
            }
            color={IconColors.DARK}
            size={IconSizes.SMALL}
          />
        </span>
      );
    };
    return (
      <>
        {isInvalid ? getErrorSuffix() : null}
        {type === InputTypes.SEARCH && getSearchSuffix()}
        {!isDisabled && type === InputTypes.PASSWORD && getPasswordSuffix()}
        {suffix ? getSuffix() : null}
      </>
    );
  };

  return (
    <div className={classNames}>
      {getLabel()}
      <span className={wrapperClassNames}>
        <div className="left">
          {getLeftAddon()}
          {renderPrefix()}
        </div>
        {multiline ? (
          <textarea
            type={inputType}
            placeholder={placeholder}
            value={value}
            disabled={isDisabled}
            readOnly={isReadOnly}
            onChange={onChange}
            onBlur={onBlur}
            name={name}
            aria-label={ariaLabel}
            title={title}
            rows={multilineRowsCount}
            onKeyDown={(e) => handleKeyDown(e, searchAction)}
            ref={ref}
            autoFocus={autoFocus}
            onInput={onInput}
            data-testid={testIds[InputTestIds.INPUT]}
          />
        )
          : (
            <input
              type={inputType}
              placeholder={placeholder}
              value={value}
              disabled={isDisabled}
              readOnly={isReadOnly}
              onChange={handleOnChange}
              onBlur={onBlur}
              autoComplete={autoComplete}
              autoFocus={autoFocus}
              name={name}
              onPaste={handleOnPaste}
              aria-label={ariaLabel}
              title={title}
              onClick={onClick}
              onKeyDown={(e) => handleKeyDown(e, searchAction)}
              onKeyUp={onKeyUp}
              ref={ref}
              max={type === InputTypes.NUMBER ? max : maxCharAllowed}
              onInput={onInput}
              data-testid={testIds[InputTestIds.INPUT]}
            />
          )}
        <div className="right">
          {renderSuffix()}
          {getRightAddon()}
        </div>
      </span>
      {getHintText()}
    </div>
  );
});

Input.defaultProps = {
  type: InputTypes.TEXT,
  className: '',
  placeholder: null,
  value: '',
  label: '',
  isDisabled: false,
  isReadOnly: false,
  isInvalid: false,
  hintText: '',
  leftAddOn: null,
  rightAddOn: null,
  prefix: null,
  suffix: null,
  multiline: false,
  onChange: () => {},
  onBlur: () => {},
  onKeyUp: () => {},
  searchAction: null,
  autoComplete: 'new-password',
  inputProps: {},
  labelHint: '',
  disablePaste: false,
  multilineRowsCount: 3,
  suffixTooltip: '',
  min: -Infinity,
  max: Infinity,
  allowDecimal: true,
  maxCharAllowed: Infinity,
  autoFocus: false,
  onClick: () => {},
  onInput: () => {},
  testIds: {},
};

Input.propTypes = {
  type: PropTypes.oneOf(Object.values(InputTypes)),
  className: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  label: PropTypes.string,
  labelHint: PropTypes.string,
  multiline: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  isInvalid: PropTypes.bool,
  hintText: PropTypes.string,
  leftAddOn: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  rightAddOn: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  prefix: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  suffix: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onKeyUp: PropTypes.func,
  searchAction: PropTypes.func,
  autoComplete: PropTypes.string,
  inputProps: PropTypes.shape({
    name: PropTypes.string,
    title: PropTypes.string,
    ariaLabel: PropTypes.string,
  }),
  disablePaste: PropTypes.bool,
  multilineRowsCount: PropTypes.number,
  suffixTooltip: PropTypes.string,
  min: PropTypes.number,
  max: PropTypes.number,
  allowDecimal: PropTypes.bool,
  maxCharAllowed: PropTypes.number,
  autoFocus: PropTypes.bool,
  onClick: PropTypes.func,
  onInput: PropTypes.func,
  testIds: PropTypes.shape({}),
};

export {
  Input as default,
  InputTypes,
  InputTestIds,
};
